"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordSessionStop = exports.recordSession = exports.reportError = exports.setPageName = exports.setRouteName = exports.setUser = exports.addMetadata = exports.addBreadcrumb = exports.getLastTrackedEvent = exports.trackEventSinceLastAction = exports.trackEvent = exports.setEventLevel = exports.globalEventLevel = exports.reporters = void 0;
const utils_1 = require("../logger/utils");
const utils_2 = require("../utils");
exports.reporters = {};
/**
 * Sets the global event level.
 */
function setEventLevel(level) {
    exports.globalEventLevel = level !== null && level !== void 0 ? level : undefined;
}
exports.setEventLevel = setEventLevel;
/**
 * Gets reporters, optionally with filters.
 * @param filters Filters to remove or specify specific reporters.
 * @returns Selected reporters, or all registered reporters.
 */
function getReporters(filters) {
    let result = Object.entries(exports.reporters);
    if (filters === null || filters === void 0 ? void 0 : filters.excludeReporters) {
        result = result.filter(([key]) => { var _a; return !((_a = filters === null || filters === void 0 ? void 0 : filters.excludeReporters) === null || _a === void 0 ? void 0 : _a.includes(key)); });
    }
    if (filters === null || filters === void 0 ? void 0 : filters.toReporters) {
        result = result.filter(([key]) => { var _a; return (_a = filters === null || filters === void 0 ? void 0 : filters.toReporters) === null || _a === void 0 ? void 0 : _a.includes(key); });
    }
    return result.map(([, reporter]) => reporter);
}
/**
 * Tracks an Analytics event.
 */
function trackEvent(event) {
    var _a;
    if (exports.globalEventLevel && event.level && !(0, utils_1.isAboveLevel)(event.level, exports.globalEventLevel)) {
        return;
    }
    for (const reporter of getReporters(event)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.trackEvent) === false) {
            continue;
        }
        else if (reporter.eventLevel && event.level && !(0, utils_1.isAboveLevel)(event.level, reporter.eventLevel)) {
            return;
        }
        const reporterEvent = { ...event };
        reporterEvent.metadata = (0, utils_2.filterReporterMetadata)(event.metadata, reporter);
        reporterEvent.metrics = (0, utils_2.filterReporterMetadata)(event.metrics, reporter);
        reporterEvent.tags = (0, utils_2.filterReporterMetadata)(event.tags, reporter);
        reporter.trackEvent(reporterEvent);
    }
}
exports.trackEvent = trackEvent;
/**
 * Tracks an Analytics event, recording the time since the last event, if available.
 */
function trackEventSinceLastAction(event) {
    if (exports.globalEventLevel && event.level && !(0, utils_1.isAboveLevel)(event.level, exports.globalEventLevel)) {
        return;
    }
    const lastEvent = getLastTrackedEvent();
    if (lastEvent) {
        const duration = new Date().getTime() - lastEvent.occurred.getTime();
        trackEvent({
            ...event,
            metadata: {
                ...event.metadata,
                lastEventName: lastEvent.message,
                timeSinceLastEvent: duration,
            },
        });
    }
    else {
        trackEvent(event);
    }
    sessionStorage.setItem('loggerLastEvent', JSON.stringify({ ...event, occurred: new Date() }));
}
exports.trackEventSinceLastAction = trackEventSinceLastAction;
/**
 * Gets the last tracked event, if available.
 */
function getLastTrackedEvent() {
    const eventStr = sessionStorage.getItem('loggerLastEvent');
    if (!eventStr)
        return null;
    const event = JSON.parse(eventStr);
    event.occurred = new Date(event.occurred);
    return event;
}
exports.getLastTrackedEvent = getLastTrackedEvent;
/**
 * Breadcrumbs to create a trail of events that happened prior to an issue.
 * These events are very similar to traditional logs, but can record more rich structured data.
 */
function addBreadcrumb(breadcrumb) {
    var _a, _b;
    for (const reporter of getReporters(breadcrumb)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.addBreadcrumb) === false) {
            continue;
        }
        else if (breadcrumb.category && ((_b = reporter.ignoreBreadcrumbCategories) === null || _b === void 0 ? void 0 : _b.includes(breadcrumb.category))) {
            continue;
        }
        const reporterBreadcrumb = { ...breadcrumb };
        reporterBreadcrumb.metadata = (0, utils_2.filterReporterMetadata)(breadcrumb.metadata, reporter);
        reporter.addBreadcrumb(reporterBreadcrumb);
    }
}
exports.addBreadcrumb = addBreadcrumb;
/**
 * Adds global metadata to all events and logs.
 * @param metadata Metadata to add.
 * @param filters Optional filters to specify which reporters to add metadata to.
 */
function addMetadata(metadata, filters) {
    var _a;
    for (const reporter of getReporters(filters)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.addMetadata) === false) {
            continue;
        }
        reporter.addMetadata((0, utils_2.filterReporterMetadata)(metadata, reporter));
    }
}
exports.addMetadata = addMetadata;
/**
 * Sets the user information in Analytics.
 * @param user User information, or null to clear.
 * @param filters Optional filters to specify which reporters to set the user for.
 */
function setUser(user, filters) {
    var _a;
    for (const reporter of getReporters(filters)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.setUser) === false) {
            continue;
        }
        reporter.setUser(user);
    }
}
exports.setUser = setUser;
/**
 * Sets the route name in Analytics.
 * Some Analytics services use this differentiate SPA Route vs Page changes.
 * @param routeName Name of the Route.
 * @param filters Optional filters to specify which reporters to set the route name for.
 */
function setRouteName(routeName, filters) {
    var _a;
    for (const reporter of getReporters(filters)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.setRouteName) === false) {
            continue;
        }
        reporter.setRouteName(routeName);
    }
}
exports.setRouteName = setRouteName;
/**
 * Sets the page name in Analytics.
 * @param pageName Name of the Page.
 * @param filters Optional filters to specify which reporters to set the page name for.
 */
function setPageName(pageName, filters) {
    var _a;
    for (const reporter of getReporters(filters)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.setPageName) === false) {
            continue;
        }
        reporter.setPageName(pageName);
    }
}
exports.setPageName = setPageName;
/**
 * Reports an Error in Reporters that support error tracking.
 * @param error Error data.
 * @param metadata Metadata to add to the error.
 * @param filters Optional filters to specify which reporters to report the error to.
 */
function reportError(error, metadata, filters) {
    var _a, _b;
    for (const reporter of getReporters(filters)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.reportError) === false) {
            continue;
        }
        (_b = reporter.reportError) === null || _b === void 0 ? void 0 : _b.call(reporter, error, (0, utils_2.filterReporterMetadata)(metadata, reporter));
    }
}
exports.reportError = reportError;
/**
 * Starts a session recording in Analytics, e.g. RUM Session Replay
 * @param filters Optional filters to specify which reporters to start a session recording for.
 */
function recordSession(filters) {
    var _a, _b;
    for (const reporter of getReporters(filters)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.recordSession) === false) {
            continue;
        }
        (_b = reporter.recordSession) === null || _b === void 0 ? void 0 : _b.call(reporter);
    }
}
exports.recordSession = recordSession;
/**
 * Stops a session recording in Analytics, e.g. RUM Session Replay
 * @param filters Optional filters to specify which reporters to stop a session recording for.
 */
function recordSessionStop(filters) {
    var _a, _b;
    for (const reporter of getReporters(filters)) {
        if (((_a = reporter.endpoints) === null || _a === void 0 ? void 0 : _a.recordSessionStop) === false) {
            continue;
        }
        (_b = reporter.recordSessionStop) === null || _b === void 0 ? void 0 : _b.call(reporter);
    }
}
exports.recordSessionStop = recordSessionStop;
//# sourceMappingURL=index.js.map